import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpService } from '../../../utility/http-service';
import { RecentlyViewedService } from '../../../services/recently-viewed.service';

export interface UserLookup {
  id: string;
  displayName: string;
  email: string;
}

export interface ClientLinkDetail {
  clientId: string;
  displayName: string | null;
  accountId: string | null;
  extClientId: number | null;
  msaSigned: boolean | null;
  msaSignedDate: string | null;
  msaSignedBy: string | null;
  msaSignedByName: string | null;
  billingFrequency: string | null;
  billingRate: number | null;
}

export interface VarianceRow {
  id: string;
  clientId: string;
  varianceDate: string;
  wellCount: number;
  responsibleUser: string;
  responsibleUserName: string;
}

export interface BillingOwnerRow {
  id: string;
  clientId: string;
  ownerEmail: string;
}

export interface WildcardRuleRow {
  id: string;
  clientId: string;
  wildcardText: string;
  negate: boolean | null;
  editing?: boolean;
}

@Component({
  selector: 'app-prod-data-detail',
  templateUrl: './prod-data-detail.component.html',
  styleUrl: './prod-data-detail.component.css',
  standalone: false
})
export class ProdDataDetailComponent implements OnInit {
  clientId!: string;
  clientName = '';
  activeTab = 'general';

  // General
  link: ClientLinkDetail | null = null;
  linkLoading = true;
  billingOptions = ['Monthly', 'Quarterly', 'Annual'];
  users: UserLookup[] = [];
  auditDownloading = false;
  opBillingRunning = false;
  opBillingQueued = false;

  // Variance history
  variance: VarianceRow[] = [];
  varianceLoading = true;
  showAddVariance = false;
  newVariance = { varianceDate: '', wellCount: 0, responsibleUser: '' };

  // Billing owners
  billingOwners: BillingOwnerRow[] = [];
  billingLoading = true;
  showAddBilling = false;
  newBillingEmail = '';

  // Wildcard rules
  wildcardRules: WildcardRuleRow[] = [];
  wildcardLoading = true;
  showAddWildcard = false;
  newWildcard = { wildcardText: '', negate: false };

  constructor(private route: ActivatedRoute, private http: HttpService, private recentlyViewed: RecentlyViewedService) {}

  ngOnInit(): void {
    this.clientId = this.route.snapshot.paramMap.get('clientId')!;
    this.clientName = history.state?.displayName ?? this.clientId;
    // If the display name arrived via router state, record it now; otherwise
    // loadLink() records once the name resolves from the API.
    if (history.state?.displayName) this.recordRecentlyViewed();
    this.http.get<UserLookup[]>('ProdData/GetUsers', (res: UserLookup[]) => {
      this.users = res;
    });
    this.loadLink();
    this.loadVariance();
    this.loadBillingOwners();
    this.loadWildcards();
    // Default variance date to today (CST = UTC-6)
    const now = new Date();
    const cstOffset = -6 * 60;
    const cst = new Date(now.getTime() + (cstOffset - now.getTimezoneOffset()) * 60000);
    this.newVariance.varianceDate = cst.toISOString().substring(0, 10);
  }

  // ── General ───────────────────────────────────────────────────────────────

  loadLink(): void {
    this.http.get<ClientLinkDetail>(
      `ProdData/GetClientLink?clientId=${this.clientId}`,
      (res: ClientLinkDetail) => {
        this.link = res;
        if (res.displayName) this.clientName = res.displayName;
        this.linkLoading = false;
        this.recordRecentlyViewed();
      }
    );
  }

  /** Add this client's Production Data screen to the Recently Viewed list. */
  private recordRecentlyViewed(): void {
    if (!this.clientId) return;
    this.recentlyViewed.add({
      label: this.clientName || this.clientId,
      url: `/prod-data/${this.clientId}`,
      icon: 'database',
      type: 'client',
      colorKey: 'amber',
      subtitle: 'Production Data',
      timestamp: Date.now()
    });
  }

  saveLink(): void {
    if (!this.link) return;
    this.http.post('ProdData/UpdateClientLink', () => {}, null, this.link);
  }

  getUserName(id: string | null): string {
    if (!id) return '';
    return this.users.find(u => u.id === id)?.displayName ?? id;
  }

  downloadAccountPermissions(): void {
    this.auditDownloading = true;
    this.http.getFile(
      `ProdData/AuditAccountPermissions?clientId=${this.clientId}`,
      `AccountPermissions_${this.clientId}.xlsx`,
      () => { this.auditDownloading = false; }
    );
  }

  runOpBilling(): void {
    this.opBillingRunning = true;
    this.opBillingQueued = false;
    this.http.get(
      `ProdData/RunOpBilling?clientId=${this.clientId}`,
      () => {
        this.opBillingRunning = false;
        this.opBillingQueued = true;
      }
    );
  }

  // ── Variance History ──────────────────────────────────────────────────────

  loadVariance(): void {
    this.http.get<VarianceRow[]>(
      `ProdData/GetVarianceHistory?clientId=${this.clientId}`,
      (res: VarianceRow[]) => {
        this.variance = res;
        this.varianceLoading = false;
      }
    );
  }

  addVariance(): void {
    const payload = {
      clientId: this.clientId,
      varianceDate: new Date(this.newVariance.varianceDate).toISOString(),
      wellCount: this.newVariance.wellCount,
      responsibleUser: this.newVariance.responsibleUser
    };
    this.http.post('ProdData/AddVarianceHistory', () => {
      this.showAddVariance = false;
      this.newVariance = { varianceDate: this.newVariance.varianceDate, wellCount: 0, responsibleUser: '' };
      this.loadVariance();
    }, null, payload);
  }

  deleteVariance(row: VarianceRow): void {
    if (!confirm('Delete this variance record?')) return;
    this.http.post(`ProdData/DeleteVarianceHistory?id=${row.id}`, () => {
      this.variance = this.variance.filter(v => v.id !== row.id);
    });
  }

  // ── Billing Owners ────────────────────────────────────────────────────────

  loadBillingOwners(): void {
    this.http.get<BillingOwnerRow[]>(
      `ProdData/GetBillingOwners?clientId=${this.clientId}`,
      (res: BillingOwnerRow[]) => {
        this.billingOwners = res;
        this.billingLoading = false;
      }
    );
  }

  addBillingOwner(): void {
    this.http.post('ProdData/AddBillingOwner', () => {
      this.showAddBilling = false;
      this.newBillingEmail = '';
      this.loadBillingOwners();
    }, null, { clientId: this.clientId, ownerEmail: this.newBillingEmail });
  }

  deleteBillingOwner(row: BillingOwnerRow): void {
    if (!confirm(`Remove ${row.ownerEmail}?`)) return;
    this.http.post(`ProdData/DeleteBillingOwner?id=${row.id}`, () => {
      this.billingOwners = this.billingOwners.filter(b => b.id !== row.id);
    });
  }

  // ── Wildcard Rules ────────────────────────────────────────────────────────

  loadWildcards(): void {
    this.http.get<WildcardRuleRow[]>(
      `ProdData/GetWildcardRules?clientId=${this.clientId}`,
      (res: WildcardRuleRow[]) => {
        this.wildcardRules = res;
        this.wildcardLoading = false;
      }
    );
  }

  addWildcard(): void {
    this.http.post('ProdData/AddWildcardRule', () => {
      this.showAddWildcard = false;
      this.newWildcard = { wildcardText: '', negate: false };
      this.loadWildcards();
    }, null, { clientId: this.clientId, ...this.newWildcard });
  }

  saveWildcard(rule: WildcardRuleRow): void {
    this.http.postReturn('ProdData/UpdateWildcardRule', () => {
      rule.editing = false;
    }, null, { id: rule.id, wildcardText: rule.wildcardText, negate: rule.negate });
  }

  deleteWildcard(rule: WildcardRuleRow): void {
    if (!confirm('Delete this wildcard rule?')) return;
    this.http.post(`ProdData/DeleteWildcardRule?id=${rule.id}`, () => {
      this.wildcardRules = this.wildcardRules.filter(r => r.id !== rule.id);
    });
  }
}
